import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { SectionHeader } from '../components/PageHeader';
import { useToast } from '../toast';

interface SignupCode {
  id: string;
  prefix: string;
  note: string;
  createdAt: string;
  createdBy: string | null;
  expiresAt: string;
  redeemedAt: string | null;
  redeemedBy: string | null;
  revokedAt: string | null;
  tenantId: string | null;
  tenantName: string | null;
  status: 'open' | 'used' | 'expired' | 'revoked';
}

interface Minted {
  code: string;
  link: string;
  expiresAt: string;
}

/**
 * Invite codes, for closed testing. Operator-only: a code is permission to create a workspace on the
 * platform, which is not a tenant's decision to make.
 *
 * The code itself is shown once, when it is made. Only its hash is kept, so there is no screen anywhere
 * that can show it again — a code that goes astray is revoked and replaced, not looked up.
 */
export function InviteCodes() {
  const [codes, setCodes] = useState<SignupCode[]>([]);
  const [note, setNote] = useState('');
  const [days, setDays] = useState(14);
  const [minted, setMinted] = useState<Minted | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const page = await api<{ codes: SignupCode[] }>('/api/admin/signup-codes');
      setCodes(page.codes);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the invite codes');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  function mint(e: FormEvent) {
    e.preventDefault();
    setMinted(null);
    void act(async () => {
      const created = await api<Minted>('/api/admin/signup-codes', { method: 'POST', body: { note, expiresDays: days } });
      setMinted(created);
      setNote('');
    });
  }

  return (
    <>
      <SectionHeader
        title="Invite codes"
        blurb="One code lets one person create one workspace and own it. Codes are how anybody gets in while the platform is in closed testing."
      />
      {error && <div className="alert">{error}</div>}

      <div className="card">
        <h3>New code</h3>
        <form onSubmit={mint}>
          <div className="row">
            <input
              id="code-note"
              aria-label="Who is it for"
              placeholder="Who is it for? (e.g. Dana at Acme)"
              maxLength={200}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <select id="code-days" aria-label="Expires after" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
          <button type="submit" className="block" disabled={busy}>Create a code</button>
        </form>

        {minted && (
          <div className="ok" style={{ marginTop: 12 }}>
            <p className="small">
              Copy this now — it is not stored and cannot be shown again. It works once, and expires on{' '}
              {new Date(minted.expiresAt).toLocaleDateString()}.
            </p>
            <p className="mono" style={{ fontSize: '1.3em', letterSpacing: '0.05em' }}>{minted.code}</p>
            <input readOnly value={minted.link} onFocus={(e) => e.currentTarget.select()} style={{ width: '100%' }} />
          </div>
        )}
      </div>

      <div className="card flush">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>For</th>
              <th>Status</th>
              <th>Used by</th>
              <th className="actions-cell"></th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && (
              <tr><td colSpan={5} className="empty">No codes yet.</td></tr>
            )}
            {codes.map((c) => (
              <tr key={c.id}>
                <td className="mono">{c.prefix}…</td>
                <td>
                  {c.note || <span className="muted">—</span>}
                  <br />
                  <span className="small muted">made {new Date(c.createdAt).toLocaleDateString()}</span>
                </td>
                <td>
                  <span className={`pill ${c.status === 'open' ? 'on' : 'off'}`}>{c.status}</span>
                  <br />
                  <span className="small muted">
                    {c.status === 'open' ? `expires ${new Date(c.expiresAt).toLocaleDateString()}` : ''}
                  </span>
                </td>
                <td className="small">
                  {c.redeemedBy ?? <span className="muted">—</span>}
                  {c.tenantName && <><br /><span className="muted">{c.tenantName}</span></>}
                </td>
                <td className="actions-cell">
                  {c.status === 'open' && (
                    <button
                      className="danger"
                      disabled={busy}
                      onClick={() => act(() => api(`/api/admin/signup-codes/${c.id}/revoke`, { method: 'POST' }))}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
